import React, { useRef, useState } from 'react';
import { Layout, Form, Input, Button, Typography, Row, Col, message, Spin, InputRef } from 'antd';
import { MailOutlined, LockOutlined } from '@ant-design/icons';
import { Link, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { login, verify2FA } from '../services/auth';
import { setCredentials } from '../store/authSlice';
import MojafinLogo from '../svg/mojafin';
import KasakaLogo from '../svg/kasaka';
import KasakaIcon from '../svg/coopwyze_icon';
import usePlatform from '../hooks/usePlatform';
import { saveToFile } from '../services/desktopUtils';
import axios from 'axios';

const { Header, Content, Footer } = Layout;
const { Title, Paragraph, Text } = Typography;

const Login: React.FC = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [otpRequired, setOtpRequired] = useState(false);
    const [email, setEmail] = useState('');
    const [loading, setLoading] = useState(false);
    const [otp, setOtp] = useState(['', '', '', '', '', '']);
    const inputRefs = useRef<(InputRef | null)[]>([]);
    const platform = usePlatform();

    const handleOtpChange = (value: string, index: number) => {
        const newOtp = [...otp];
        newOtp[index] = value;
        setOtp(newOtp);

        if (value && index < 5) {
            inputRefs.current[index + 1]?.focus();
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, index: number) => {
        if (e.key === 'Backspace' && !otp[index] && index > 0) {
            inputRefs.current[index - 1]?.focus();
        }
    };

    const storeCredentials = (access_token: string, refresh_token: string, expires_in: number, user: any) => {
        const credentials = {
            access_token,
            refresh_token,
            expires_in,
            user,
        };
        // Store in localStorage (Web and Electron)
        localStorage.setItem('auth', JSON.stringify(credentials));
        // Store in file (Electron only)
        if (platform === 'electron') {
            saveToFile(JSON.stringify(credentials), 'auth.json');
        }
        // Dispatch to Redux
        dispatch(setCredentials(credentials));
        
    };

    const onFinish = async (values: { email: string; password: string }) => {
        setLoading(true);
        try {
            const response = await login(values.email, values.password);
            if (response.requires_2fa) {
                setOtpRequired(true);
                setEmail(values.email);
                message.info(response.message);
            } else {
                storeCredentials(response.access_token, response.refresh_token, response.expires_in, response.user);
                navigate('/');
            }
        } catch (error) {
            if (axios.isAxiosError(error) && error.response) {
                message.error(error.response.data.message || 'Login failed. Please check your credentials.');
            } else {
                message.error('An unexpected error occurred. Please try again.');
            }
        } finally {
            setLoading(false);
        }
    };

    const handleOtpVerify = async () => {
        setLoading(true);
        try {
            const response = await verify2FA(email, otp.join(''));
            storeCredentials(response.access_token, response.refresh_token, response.expires_in, response.user);
            navigate('/');
        } catch (error) {
            if (axios.isAxiosError(error) && error.response) {
                message.error(error.response.data.message || '2FA verification failed. Please try again.');
            } else {
                message.error('An unexpected error occurred. Please try again.');
            }
        } finally {
            setLoading(false);
        }
    };

    
    return (
        <Layout
            style={{
                minHeight: "100vh",
                position: "relative",
                overflow: "hidden",
                backgroundColor: "transparent",
            }}
        >

            {/* SVG Background */}
            <div
                style={{
                    position: "fixed",
                    top: 0,
                    left: 0,
                    width: "100vw",
                    height: "100vh",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    zIndex: 0,
                    opacity: 0.1,
                    pointerEvents: "none",
                    overflow: "hidden"
                }}
            >
                <div
                    style={{
                        minWidth: "100%",
                        minHeight: "100%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        padding: "5rem" // Added 5rem (80px) padding
                    }}
                >
                    <KasakaIcon
                        width="min(calc(100vw - 10rem), 1600px)"
                        height="min(calc(100vh - 10rem), 1600px)"
                    />
                </div>
            </div>
            <Header
                style={{
                    position: "fixed",
                    zIndex: 1,
                    width: "100%",
                    backgroundColor: "#FFFFFF",
                }}
            >
                <Row justify="space-between" align="middle">
                    <Col>
                        <Link to="/">
                            <KasakaLogo />
                        </Link>
                    </Col>
                    
                </Row>
            </Header>

            <Content
                style={{
                    padding: "20px",
                    minHeight: "calc(100vh - 64px - 64px)",
                    backgroundColor: "#FFFFFF",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                }}
            >
                <Spin spinning={loading} tip="Processing...">
                    <div style={{ maxWidth: 400, width: "100%" }}>
                        <div style={{ textAlign: "center", marginBottom: 24 }}>
                            <Title level={2}>
                                {otpRequired ? "Enter 6-digit OTP" : "Sign in to your account"}
                            </Title>
                        </div>

                        {otpRequired ? (
                            <Form
                                name="otp"
                                onFinish={handleOtpVerify}
                                autoComplete="off"
                            >
                                <Form.Item
                                    name="otp"
                                    rules={[{ required: true, message: "Please input the OTP!" }]}
                                >
                                    <Row gutter={8} justify="space-between">
                                        {otp.map((digit, index) => (
                                            <Col span={4} key={index}>
                                                <Input
                                                    ref={el => inputRefs.current[index] = el}
                                                    value={digit}
                                                    onChange={e => handleOtpChange(e.target.value, index)}
                                                    onKeyDown={e => handleKeyDown(e, index)}
                                                    maxLength={1}
                                                    size="large"
                                                    style={{
                                                        width: '100%',
                                                        textAlign: 'center',
                                                        borderRadius: 8
                                                    }}
                                                />
                                            </Col>
                                        ))}
                                    </Row>
                                </Form.Item>

                                <Form.Item>
                                    <Button
                                        type="primary"
                                        htmlType="submit"
                                        size="large"
                                        style={{ width: "100%", borderRadius: 8 }}
                                        disabled={loading || otp.some(digit => !digit)}
                                    >
                                        Verify
                                    </Button>
                                </Form.Item>
                            </Form>
                        ) : (
                            <Form
                                name="login"
                                initialValues={{ remember: true }}
                                onFinish={onFinish}
                                autoComplete="off"
                            >
                                <Form.Item
                                    name="email"
                                    rules={[{ required: true, message: "Please input your email!" }]}
                                >
                                    <Input
                                        prefix={<MailOutlined />}
                                        placeholder="Email address"
                                        size="large"
                                        style={{ borderRadius: 8 }}
                                    />
                                </Form.Item>

                                <Form.Item
                                    name="password"
                                    rules={[
                                        { required: true, message: "Please input your password!" },
                                    ]}
                                >
                                    <Input.Password
                                        prefix={<LockOutlined />}
                                        placeholder="Password"
                                        size="large"
                                        style={{ borderRadius: 8 }}
                                    />
                                </Form.Item>

                                <Form.Item>
                                    <Button
                                        type="primary"
                                        htmlType="submit"
                                        size="large"
                                        style={{ width: "100%", borderRadius: 8 }}
                                        disabled={loading}
                                    >
                                        Sign in
                                    </Button>
                                </Form.Item>
                            </Form>
                        )}

                        <Paragraph style={{ textAlign: "center", marginTop: 16 }}>
                            Forgot your password?{" "}
                            <a href="/" style={{ color: "#1890ff" }}>
                                Request Password Reset
                            </a>
                        </Paragraph>
                    </div>
                </Spin>
            </Content>

            <Footer
                style={{
                    backgroundColor: "#FFFFFF",
                    padding: "24px 50px",
                    display: "flex",
                    flexWrap: "wrap",
                    justifyContent: "space-between",
                }}
            >
                <Text
                    style={{ fontSize: 14, color: "rgba(0, 0, 0, 0.45)", flex: 1 }}
                >
                    © {new Date().getFullYear()}{" "}
                    <a href="https://www.afrocentec.com/">Afrocentec</a>. All rights reserved.
                </Text>
                <Text
                    style={{
                        fontSize: 14,
                        color: "rgba(0, 0, 0, 0.45)",
                        display: "inline-flex",
                        justifyContent: "flex-end",
                    }}
                >
                    <MojafinLogo />
                </Text>
            </Footer>
        </Layout>
    );
};

export default Login;

